// MIT License
// 
// Copyright (c) 2017 Dmitriy Kovalenko
// 
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
// 
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
// 
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

// This code has been modified in part by Sam Cristall (cristall@ejectedplanet.com)

/* eslint-disable no-param-reassign */
import * as React from 'react';

import clsx from 'clsx';

import { IconButton, withStyles } from '@material-ui/core';
import { DatePicker } from 'material-ui-pickers';

import {
  endOfWeek,
  format,
  isSameDay,
  isValid,
  isWithinInterval,
  startOfWeek,
} from 'date-fns';

const styles = (theme: any) => ({
  customDayHighlight: {
    border: `1px solid ${theme.palette.secondary.main}`,
    borderRadius: '50%',
    bottom: 0,
    left: '2px',
    position: 'absolute' as 'relative',
    right: '2px',
    top: 0,
  },
  day: {
    color: 'inherit',
    fontSize: theme.typography.caption.fontSize,
    height: 36,
    margin: '0 2px',
    width: 36,
  },
  dayWrapper: {
    position: 'relative' as 'relative',
  },
  endHighlight: {
    borderBottomRightRadius: '50%',
    borderTopRightRadius: '50%',
    extend: 'highlight' as 'highlight',
  },
  firstHighlight: {
    borderBottomLeftRadius: '50%',
    borderTopLeftRadius: '50%',
    extend: 'highlight' as 'highlight',
  },
  highlight: {
    background: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  highlightNonCurrentMonthDay: {
    color: '#676767',
  },
  nonCurrentMonthDay: {
    color: theme.palette.text.disabled,
  },
});

class WeekPicker extends React.Component<any, any> {
  public constructor(props: any) {
    super(props);
  }

  public render() {
    const { date } = this.props;

    return (
      <div className="picker">
        <DatePicker
          value={date}
          onChange={this.handleWeekChange}
          renderDay={this.renderWrappedWeekDay}
          labelFunc={this.formatWeekSelectLabel}
        />
      </div>
    );
  }

  private handleWeekChange = (date: any) => {
    this.props.onWeekChange(date);
  }

  private formatWeekSelectLabel = (date: any, invalidLabel: any) => {
    if (date === null) {
      return '';
    }

    return date && isValid(date)
      ? `Week of ${format(startOfWeek(date), 'MMM do')}`
      : invalidLabel;
  }

  private renderWrappedWeekDay = (
      date: any,
      selectedDate: any,
      dayInCurrentMonth: any) => {
    const { classes } = this.props;

    const start = startOfWeek(selectedDate);
    const end = endOfWeek(selectedDate);

    const dayIsBetween = isWithinInterval(date, { start, end });
    const isFirstDay = isSameDay(date, start);
    const isLastDay = isSameDay(date, end);

    const wrapperClassName = clsx({
      [classes.highlight]: dayIsBetween,
      [classes.firstHighlight]: isFirstDay,
      [classes.endHighlight]: isLastDay,
    });

    const dayClassName = clsx(classes.day, {
      [classes.nonCurrentMonthDay]: !dayInCurrentMonth,
      [classes.highlightNonCurrentMonthDay]: !dayInCurrentMonth && dayIsBetween,
    });

    return (
      <div className={wrapperClassName}>
        <IconButton className={dayClassName}>
          <span> { format(date, 'd')} </span>
        </IconButton>
      </div>
    );
  }

}


export default withStyles(styles)(WeekPicker);
